import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export type ChartPeriod = "today" | "week" | "month";

interface SalesChartProps {
  labels: string[];
  data: number[];
  currencySymbol?: string;
  period: ChartPeriod;
  onPeriodChange: (period: ChartPeriod) => void;
}

const PERIOD_OPTIONS: { value: ChartPeriod; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const SalesChart = ({
  labels,
  data,
  currencySymbol = "",
  period,
  onPeriodChange,
}: SalesChartProps) => {
  const chartData = labels.map((label, index) => ({
    label,
    sales: data[index] ?? 0,
  }));

  return (
    <div className="bg-white rounded-xl shadow-md p-6 outline-none">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-[#402B69]">Sales Overview</h2>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onPeriodChange(option.value)}
              className={`px-3 py-1.5 text-xs font-semibold transition ${
                period === option.value
                  ? "bg-[#885ABB] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
          <XAxis dataKey="label" stroke="#888888" />
          <YAxis stroke="#888888" />
          <Tooltip
            formatter={(value) => [`${currencySymbol}${value ?? 0}`, "Sales"]}
            contentStyle={{ borderRadius: "8px", border: "1px solid #E5E5E5" }}
          />
          <Line
            type="monotone"
            dataKey="sales"
            stroke="#885ABB"
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SalesChart;
